import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { permissionGroupsApi, PermissionGroup, SystemFunction, ActionOption } from '../api/permissionGroupsApi';
import { api } from '../api/index';
import { groupFunctions, getGroupedPermission, updateGroupedPermission, GroupedFunction } from '../utils/functionGrouping';

// Helper to check if code has CRUD suffix (imported from utils)
function hasCrudSuffix(code: string): boolean {
  const crudSuffixes = ['_LIST', '_VIEW', '_ADD', '_EDIT', '_DELETE', '_REMOVE', '_IMPORT'];
  return crudSuffixes.some(suffix => code.endsWith(suffix));
}

interface Building {
  id: string;
  name: string;
  address: string;
}

export const PermissionGroupEdit: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [permissionGroup, setPermissionGroup] = useState<PermissionGroup | null>(null);
  const [availableFunctions, setAvailableFunctions] = useState<SystemFunction[]>([]);
  const [groupedFunctions, setGroupedFunctions] = useState<GroupedFunction[]>([]);
  const [availableActions, setAvailableActions] = useState<ActionOption[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as { functionId: string; actions: string[] }[],
  });

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    try {
      setLoadingData(true);
      const [group, functions, actions] = await Promise.all([
        permissionGroupsApi.getById(id),
        permissionGroupsApi.getAvailableFunctions(),
        permissionGroupsApi.getAvailableActions(),
      ]);

      setPermissionGroup(group);
      setAvailableFunctions(functions);
      
      // Group functions by base name
      const grouped = groupFunctions(functions);
      setGroupedFunctions(grouped);
      
      // Map action values to translated labels
      const translatedActions = actions.map(action => ({
        value: action.value,
        label: t(action.value) || action.label // Use translation key if available, fallback to original label
      }));
      setAvailableActions(translatedActions);
      
      // Initialize form data with existing permissions
      setFormData({
        name: group.name,
        description: group.description || '',
        permissions: group.permissions.map(p => ({
          functionId: p.functionId,
          actions: p.actions,
        })),
      });
    } catch (error: any) {
      console.error('Error loading data:', error);
      alert(error.response?.data?.message || t('errorLoadingPermissionGroup'));
      navigate('/permission-groups');
    } finally {
      setLoadingData(false);
    }
  };

  // Removed buildings loaders (no longer used)

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionChange = (groupedFunction: GroupedFunction, action: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: updateGroupedPermission(prev.permissions, groupedFunction, action, checked),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(t('pleaseEnterGroupName'));
      return;
    }

    if (!id) return;

    // Filter out permissions with no actions
    const permissionsWithActions = formData.permissions.filter(perm => perm.actions.length > 0);
    
    const submitData = {
      name: formData.name,
      description: formData.description,
      permissions: permissionsWithActions,
    };

    try {
      setLoading(true);
      await permissionGroupsApi.update(id, submitData);
      alert(t('permissionGroupUpdatedSuccess'));
      navigate('/permission-groups');
    } catch (error: any) {
      alert(error.response?.data?.message || t('errorUpdatingPermissionGroup'));
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (!permissionGroup) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="text-center py-8">
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('permissionGroupNotFound')}</h3>
          <button
            onClick={() => navigate('/permission-groups')}
            className="text-cyan-600 hover:text-cyan-700"
          >
            {t('backToPermissionGroups')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>{t('accountManagement')}</span> / 
          <span className="text-cyan-600 mx-1">{t('permissionGroups')}</span> / 
          <span className="text-gray-900">{t('edit')} "{permissionGroup.name}"</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('editPermissionGroup')}</h1>
        <p className="text-gray-600">{t('updatePermissionGroupSettings')}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Basic Info */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('generalInfo')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('groupName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterGroupName')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterGroupDescription')}
                  />
                </div>
              </div>
            </div>

            {/* Removed Building Assignment for Permission Groups (no longer used) */}

            {/* Group Statistics */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('groupStatistics')}</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('assignedUsers')}:</span>
                  <span className="font-medium text-gray-900">{permissionGroup._count.users}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('functionsWithPermissions')}:</span>
                  <span className="font-medium text-gray-900">{permissionGroup.permissions.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('created')}:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(permissionGroup.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Permissions */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('functionPermissions')}</h2>
              
              {availableFunctions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">{t('noFunctionsAvailable')}</p>
                  <button
                    type="button"
                    onClick={() => navigate('/functions')}
                    className="text-cyan-600 hover:text-cyan-700"
                  >
                    {t('goToFunctions')}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Actions Header */}
                  <div
                    className="grid gap-2 pb-2 border-b border-gray-200"
                    style={{ gridTemplateColumns: `minmax(0, 2fr) repeat(${availableActions.length}, minmax(0, 1fr))` }}
                  >
                    <div className="font-medium text-sm text-gray-700">{t('function')}</div>
                    {availableActions.map(action => (
                      <div key={action.value} className="text-center font-medium text-sm text-gray-700">
                        {action.label}
                      </div>
                    ))}
                  </div>

                  {/* Function Permissions - Grouped */}
                  {groupedFunctions.map(group => {
                    return (
                      <div
                        key={group.baseCode}
                        className="grid gap-2 py-3 border-b border-gray-100"
                        style={{ gridTemplateColumns: `minmax(0, 2fr) repeat(${availableActions.length}, minmax(0, 1fr))` }}
                      >
                        <div className="flex items-center">
                          <div>
                            <div className="font-medium text-sm text-gray-900">{group.displayName}</div>
                            <div className="text-xs text-gray-500">{group.baseCode}</div>
                          </div>
                        </div>
                        {availableActions.map(action => {
                          const hasPermission = getGroupedPermission(formData.permissions, group, action.value);
                          // Only show checkbox if the action has a corresponding function
                          const hasFunction = !!group.actionToFunction[action.value];
                          return (
                            <div key={action.value} className="flex justify-center">
                              <input
                                type="checkbox"
                                checked={hasPermission}
                                disabled={!hasFunction}
                                onChange={(e) => handlePermissionChange(group, action.value, e.target.checked)}
                                className="text-cyan-600 focus:ring-cyan-500 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title={!hasFunction ? `${action.label} không khả dụng cho ${group.displayName}` : ''}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/permission-groups')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all duration-200"
          >
            {loading ? t('updating') : t('updateGroup')}
          </button>
        </div>
      </form>
    </div>
  );
}; 